import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Gift,
  Copy,
  CheckCircle,
  Link as LinkIcon,
  Share2,
  Coins,
  UserCheck,
  Clock,
  Sparkles,
  Zap,
  GraduationCap,
  ArrowRight,
  ShieldCheck,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const ReferralsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [stats, setStats] = useState(null);
  const [coinsData, setCoinsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [filterTab, setFilterTab] = useState("all"); // "all" | "completed" | "pending"

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, coinsRes] = await Promise.all([
        axios.get(`${API}/referrals/my-stats`, { withCredentials: true }),
        axios.get(`${API}/coins/my-balance`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setCoinsData(coinsRes.data);
    } catch (error) {
      console.error("Error fetching referral data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getReferralUrl = () => {
    return `${window.location.origin}/login?mode=signup&ref=${user?.referral_code}`;
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(getReferralUrl());
    toast.success("Referral link copied!");
  };

  const shareReferralLink = async () => {
    const url = getReferralUrl();
    const shareData = {
      title: "Join Jamadhan Academy",
      text: `Join Jamadhan using my referral code ${user?.referral_code} to learn institutional trading and access Drishti AI!`,
      url: url,
    };
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        toast.success("Shared successfully!");
      } catch (err) {
        if (err.name !== "AbortError") {
          toast.error("Error sharing link");
        }
      }
    } else {
      copyReferralLink();
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Referrals & Rewards">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const allReferrals = stats?.referrals || [];
  const completedReferrals = allReferrals.filter(r => r.is_completed);
  const pendingReferrals = allReferrals.filter(r => !r.is_completed);

  const displayedReferrals = filterTab === "completed" 
    ? completedReferrals 
    : filterTab === "pending" 
      ? pendingReferrals 
      : allReferrals;

  const currentCoins = coinsData?.jamadhan_coins ?? stats?.wallet_balance ?? 0;

  return (
    <DashboardLayout title="Referrals & Rewards">
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="referrals-page">
        
        {/* ═══ High-Contrast Responsive Wallet Balance Card ═══ */}
        <Card className={`border shadow-xl relative overflow-hidden rounded-2xl transition-all ${
          isLight
            ? "bg-gradient-to-r from-amber-500/10 via-amber-50/50 to-white border-amber-200/90 shadow-amber-500/5 text-slate-900"
            : "bg-gradient-to-r from-[#111622] via-[#0E131E] to-[#0A0E17] border-white/10 shadow-black/40 text-white"
        }`}>
          <div className="absolute right-0 top-0 opacity-10 pointer-events-none w-72 h-72 translate-x-1/4 -translate-y-1/4">
            <Coins className="w-full h-full text-[#FFCC00]" />
          </div>
          
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              
              {/* Wallet Balance Display */}
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#FFCC00] to-[#FF9900] flex items-center justify-center shadow-lg shadow-[#FFCC00]/25 shrink-0">
                  <Coins className="w-8 h-8 sm:w-10 sm:h-10 text-black" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold font-jetbrains uppercase tracking-wider bg-amber-500/15 text-amber-600 dark:text-[#FFCC00] border border-amber-500/30 mb-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Your Reward Wallet</span>
                  </div>
                  <div className="flex items-baseline gap-2.5">
                    <span className={`text-4xl sm:text-6xl font-black font-jetbrains tracking-tight ${
                      isLight ? "text-amber-950" : "text-[#FFCC00]"
                    }`}>
                      {currentCoins}
                    </span>
                    <span className={`text-base sm:text-xl font-bold font-outfit ${
                      isLight ? "text-amber-900/80" : "text-zinc-300"
                    }`}>
                      Coins
                    </span>
                  </div>
                  <p className="text-xs font-plex text-gray-500 dark:text-zinc-400 mt-1 flex items-center gap-1.5">
                    <span className="font-semibold text-emerald-600 dark:text-[#00FF88]">1 Coin = ₹1 Rupee</span>
                    <span>•</span>
                    <span>Redeemable &amp; Cash Equivalent</span>
                  </p>
                </div>
              </div>

              {/* Commission Structure / Rules Card */}
              <div className={`rounded-xl p-4 sm:p-5 border max-w-md w-full shadow-sm ${
                isLight 
                  ? "bg-white/80 border-amber-200/80 backdrop-blur-md" 
                  : "bg-white/[0.04] border-white/[0.08] backdrop-blur-md"
              }`}>
                <h4 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white mb-2.5 flex items-center gap-2 font-outfit">
                  <Gift className="w-4 h-4 text-emerald-500" />
                  <span>Referral Commission Structure</span>
                </h4>
                
                <div className="space-y-2 text-xs font-plex">
                  <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 text-gray-900 dark:text-white font-medium">
                      <Zap className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span>Drishti AI Pro Referral</span>
                    </div>
                    <span className="font-jetbrains font-bold text-emerald-600 dark:text-[#00FF88]">
                      +100 Coins (10%)
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                    <div className="flex items-center gap-2 text-gray-900 dark:text-white font-medium">
                      <GraduationCap className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                      <span>Course Enrollment</span>
                    </div>
                    <span className="font-jetbrains font-bold text-indigo-600 dark:text-[#818CF8]">
                      +1,500, 2,000, 2,500 Coins (10%)
                    </span>
                  </div>

                  <p className="text-[11px] text-gray-500 dark:text-zinc-400 pt-0.5 leading-relaxed">
                    ⚡ <strong>Purchase Required:</strong> Coins are credited automatically the exact moment your referred student successfully purchases!
                  </p>
                </div>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* ═══ 3 Core Referral Stats ═══ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card className="bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/[0.07] rounded-2xl shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0 text-blue-600 dark:text-blue-400">
                  <Users className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white font-jetbrains">
                    {stats?.total_referrals || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#8E8E93] font-outfit">Total Friends Invited</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/[0.07] rounded-2xl shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0 text-emerald-600 dark:text-[#00FF88]">
                  <UserCheck className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-[#00FF88] font-jetbrains">
                    {stats?.successful_referrals || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#8E8E93] font-outfit">Paid Purchases Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/[0.07] rounded-2xl shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0 text-amber-500">
                  <Coins className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-extrabold text-amber-600 dark:text-[#FFCC00] font-jetbrains">
                    ₹{stats?.total_rewards || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#8E8E93] font-outfit">Total Commission Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ═══ Personal Referral Link & Code ═══ */}
        <Card className="bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/[0.07] rounded-2xl shadow-sm" data-testid="referral-code-card">
          <CardHeader className="pb-3 border-b border-gray-100 dark:border-white/[0.06]">
            <CardTitle className="text-sm sm:text-base text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <Share2 className="w-4 h-4 text-emerald-500" strokeWidth={2} />
              <span>Your Unique Referral Link &amp; Code</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 bg-gray-50 dark:bg-[#14141B] border border-gray-200 dark:border-white/10 rounded-xl px-4 py-2.5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-gray-400 uppercase font-jetbrains font-semibold block">Referral Code</span>
                  <code className="text-base sm:text-lg font-mono font-extrabold text-emerald-600 dark:text-[#00FF88] tracking-wider">
                    {user?.referral_code || "JAM156F26EB"}
                  </code>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyReferralCode}
                  className="text-gray-500 dark:text-[#8E8E93] hover:text-emerald-500 h-8 px-3 rounded-lg"
                  data-testid="copy-code-btn"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  <span className="ml-1.5 text-xs font-semibold">{copied ? "Copied" : "Copy"}</span>
                </Button>
              </div>

              <Button
                onClick={copyReferralLink}
                className="bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/15 text-xs font-bold h-12 px-5 rounded-xl border border-gray-200 dark:border-transparent transition-all"
                data-testid="copy-link-btn"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Copy Link
              </Button>

              <Button
                onClick={shareReferralLink}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-xs h-12 px-6 rounded-xl shadow-md shadow-emerald-500/20 transition-all hover:scale-105 active:scale-95"
                data-testid="share-link-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Link
              </Button>
            </div>

            <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <p className="text-xs text-gray-600 dark:text-zinc-400 font-plex leading-relaxed">
                When anyone registers through your link, they are permanently tagged to your account. The moment they buy <strong>Drishti AI</strong> (100 coins) or enroll in a <strong>Course</strong> (1,500, 2,000, 2,500 coins — 10% commission), your commission credits automatically!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ═══ My Given Referrals Ledger ═══ */}
        <Card className="bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/[0.07] rounded-2xl shadow-sm" data-testid="my-referrals-list">
          <CardHeader className="pb-3 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm sm:text-base text-gray-900 dark:text-white font-outfit flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-500" strokeWidth={2} />
                  <span>My Referral Network ({allReferrals.length})</span>
                </CardTitle>
                <p className="text-[11px] text-gray-500 dark:text-zinc-400 font-plex mt-0.5">
                  Tracks all invited members and their purchases
                </p>
              </div>

              {/* Filter Tabs */}
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]">
                <button
                  onClick={() => setFilterTab("all")}
                  className={`text-xs font-semibold px-3 py-1 rounded-lg transition-all ${
                    filterTab === "all"
                      ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-xs"
                      : "text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
                >
                  All ({allReferrals.length})
                </button>
                <button
                  onClick={() => setFilterTab("completed")}
                  className={`text-xs font-semibold px-3 py-1 rounded-lg transition-all ${
                    filterTab === "completed"
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-[#00FF88] shadow-xs font-bold"
                      : "text-gray-500 dark:text-zinc-400 hover:text-emerald-500"
                  }`}
                >
                  Purchased ({completedReferrals.length})
                </button>
                <button
                  onClick={() => setFilterTab("pending")}
                  className={`text-xs font-semibold px-3 py-1 rounded-lg transition-all ${
                    filterTab === "pending"
                      ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 shadow-xs font-bold"
                      : "text-gray-500 dark:text-zinc-400 hover:text-amber-500"
                  }`}
                >
                  Pending ({pendingReferrals.length})
                </button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-4">
            {displayedReferrals.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white font-outfit">
                  {filterTab === "completed"
                    ? "No completed purchases yet"
                    : filterTab === "pending"
                      ? "No pending referrals"
                      : "No referrals given yet"}
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1 max-w-sm mx-auto font-plex">
                  Share your link with fellow traders. As soon as they purchase Drishti AI or a Course, your 10% commission appears here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedReferrals.map((ref, idx) => (
                  <div
                    key={ref.referral_id || idx}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border transition-all gap-3 ${
                      ref.is_completed
                        ? "bg-emerald-500/[0.04] border-emerald-500/25"
                        : "bg-gray-50 dark:bg-[#14141B] border-gray-200 dark:border-white/[0.05]"
                    }`}
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <Avatar className="w-10 h-10 border border-gray-200 dark:border-white/10 shrink-0">
                        <AvatarImage src={ref.picture} alt={ref.name} />
                        <AvatarFallback className="bg-gray-200 dark:bg-[#1E1E28] text-gray-900 dark:text-white text-xs font-bold font-outfit">
                          {ref.name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-900 dark:text-white font-outfit truncate">
                            {ref.name || "Student Trader"}
                          </p>
                          <span className="text-[11px] font-mono text-gray-400 dark:text-zinc-500">
                            ({ref.email})
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-zinc-400 font-plex flex items-center gap-2 mt-0.5">
                          <span>Joined: {ref.created_at ? new Date(ref.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Recent"}</span>
                          <span>•</span>
                          <span className="font-semibold text-gray-800 dark:text-zinc-300">
                            {ref.product_purchased || "Awaiting Purchase"}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-center">
                      {ref.is_completed ? (
                        <div className="text-right">
                          <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-[#00FF88] border border-emerald-500/30 text-xs font-jetbrains font-bold px-3 py-1">
                            +{ref.coins_awarded || ref.reward_amount || 100} Coins (₹{ref.coins_awarded || ref.reward_amount || 100})
                          </Badge>
                          <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-jetbrains font-semibold mt-0.5">
                            Purchased &amp; Credited
                          </p>
                        </div>
                      ) : (
                        <div className="text-right">
                          <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 text-xs font-jetbrains font-semibold px-2.5 py-0.5">
                            Joined (Pending Purchase)
                          </Badge>
                          <p className="text-[10px] text-gray-400 dark:text-zinc-500 font-plex mt-0.5">
                            Commission credits upon purchase
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ═══ Transaction Ledger ═══ */}
        <Card className="bg-white dark:bg-[#0D0D12] border-gray-200 dark:border-white/[0.07] rounded-2xl shadow-sm" data-testid="transaction-history">
          <CardHeader className="pb-3 border-b border-gray-100 dark:border-white/[0.06]">
            <CardTitle className="text-sm sm:text-base text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <Coins className="w-4 h-4 text-[#FFCC00]" strokeWidth={2} />
              <span>Coin Credit &amp; Transaction Ledger</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {(!coinsData?.transactions || coinsData.transactions.length === 0) ? (
              <p className="text-center text-xs text-gray-500 dark:text-zinc-400 py-8 font-plex">
                No coin credit transactions recorded yet.
              </p>
            ) : (
              <div className="space-y-2">
                {coinsData.transactions.map((txn) => (
                  <div key={txn.txn_id} className="flex items-center justify-between p-3.5 rounded-xl bg-gray-50 dark:bg-[#14141B] border border-gray-200 dark:border-white/[0.05]">
                    <div>
                      <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white font-outfit">{txn.description || "Referral commission credited"}</p>
                      <p className="text-[10px] text-gray-500 dark:text-zinc-400 font-mono mt-0.5">
                        {new Date(txn.created_at).toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`font-mono font-bold text-sm ${txn.amount > 0 ? "text-emerald-500" : "text-rose-500"}`}>
                        {txn.amount > 0 ? "+" : ""}{txn.amount} Coins
                      </span>
                      <Coins className="w-4 h-4 text-[#FFCC00]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
};

export default ReferralsPage;
